import { Component, DOCUMENT, inject } from '@angular/core';
import { MatToolbar } from '@angular/material/toolbar';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { OrganizationGoal } from './model/organization/organization-goal.model';
import { OrganizationHeritageType } from './model/organization/organization-heritage-type.model';
import { OrganizationImpactType } from './model/organization/organization-impact-type.model';
import { OrganizationProvince } from './model/organization/organization-province.model';
import { OrganizationReach } from './model/organization/organization-reach.model';
import { AppStateStore } from './store/app-state.store';
import { MatFormField, MatPrefix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { GoalService } from './services/goal.service';
import { HeritageTypeService } from './services/heritage-type.service';
import { ImpactTypeService } from './services/impact-type.service';
import { ProvinceService } from './services/province.service';
import { ReachService } from './services/reach.service';
import { combineLatest, debounceTime, distinctUntilChanged, filter, shareReplay, startWith } from 'rxjs';
import { ResponsiveService } from './services/responsive.service';
import { AsyncPipe, NgClass } from '@angular/common';
import { MatButton, MatMiniFabButton } from '@angular/material/button';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatIcon } from '@angular/material/icon';
import { map } from 'rxjs/operators';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { FiltersBottomSheet } from './components/filters-bottom-sheet/filters-bottom-sheet';
import { MatMenu, MatMenuContent, MatMenuTrigger } from '@angular/material/menu';
import { GoalColor, getGoalColor } from './util/goal-color.util';

@Component({
  selector: 'app-root',
  imports: [
    MatToolbar,
    RouterOutlet,
    MatFormField,
    ReactiveFormsModule,
    MatInput,
    MatSelect,
    MatOption,
    NgClass,
    AsyncPipe,
    MatButton,
    MatProgressBar,
    MatIcon,
    RouterLink,
    MatMiniFabButton,
    RouterLinkActive,
    MatPrefix,
    MatMenuTrigger,
    MatMenu,
    MatMenuContent,

  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  /**
   * Dependencies
   */
  public readonly appStateStore = inject(AppStateStore);
  public readonly matBottomSheet = inject(MatBottomSheet);
  private readonly goalService = inject(GoalService);
  private readonly heritageTypeService = inject(HeritageTypeService);
  private readonly impactTypeService = inject(ImpactTypeService);
  private readonly provinceService = inject(ProvinceService);
  private readonly reachService = inject(ReachService);
  public readonly responsiveService = inject(ResponsiveService);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  // State
  public showFilterHeader$ = combineLatest([
    this.router.events.pipe(
      // Filter for NavigationEnd
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.urlAfterRedirects),
      // Seed with the current URL immediately
      startWith(this.router.url),
    ),
    this.responsiveService.isMobileScreen$,
  ]).pipe(
    map(([url, isMobile]) => {
      // Robust URL check
      const isStoryPage = url.includes('/organization/');
      const isClaimPage = url.includes('/claim-ownership');
      const showHeader = !isStoryPage && !isClaimPage;

      // Hide if it's a neighbourhood page OR if it's mobile
      return showHeader && !isMobile;
    }),
    // Ensure we don't spam the UI with the same result
    distinctUntilChanged(),
    shareReplay(1),
  );

  public goalColor(goal: OrganizationGoal): GoalColor {
    return getGoalColor(goal.class);
  }

  /**
   * Filters
   */
    // Search query
  public searchQueryInput: FormControl = new FormControl();
  private searchQuery = '';
  // Goal filter
  public goalSelect = new FormControl<OrganizationGoal[] | null>([]);
  public goalList: OrganizationGoal[] = [];
  private selectedGoalIdList: string[] = [];
  // Heritage type filter
  public heritageTypeSelect = new FormControl<OrganizationHeritageType[] | null>([]);
  public heritageTypeList: OrganizationHeritageType[] = [];
  private selectedHeritageTypeIdList: string[] = [];
  // Impact type filter
  public impactTypeSelect = new FormControl<OrganizationImpactType[] | null>([]);
  public impactTypeList: OrganizationImpactType[] = [];
  private selectedImpactTypeIdList: string[] = [];
  // Province filter
  public provinceSelect = new FormControl<OrganizationProvince[] | null>([]);
  public provinceList: OrganizationProvince[] = [];
  private selectedProvinceIdList: string[] = [];
  // Reach filter
  public reachSelect = new FormControl<OrganizationReach[] | null>([]);
  public reachList: OrganizationReach[] = [];
  private selectedReachIdList: string[] = [];
  // Reset state
  private resetFiltersInProgress = false;

  /**
   * Data
   */

  constructor() {
    this.loadStoryData();
    // Filters
    this.loadFiltersData();
    this.observeFilterChanges();
    // Open Linked Data
    this.publishJsonLdOnOrganizationOpen();
  }

  /**
   * Filters
   */
  private loadFiltersData(): void {
    // Goal
    this.goalService.getGoalList()
      .subscribe((goalList: OrganizationGoal[]): OrganizationGoal[] => this.goalList = goalList);
    // Heritage type
    this.heritageTypeService.getHeritageTypeList()
      .subscribe((heritageTypeList: OrganizationHeritageType[]): OrganizationHeritageType[] => this.heritageTypeList = heritageTypeList);
    // Impact type
    this.impactTypeService.getImpactTypeList()
      .subscribe((impactTypeList: OrganizationImpactType[]): OrganizationImpactType[] => this.impactTypeList = impactTypeList);
    // Province
    this.provinceService.getProvinceList()
      .subscribe((provinceList: OrganizationProvince[]): OrganizationProvince[] => this.provinceList = provinceList);
    // Reach
    this.reachService.getReachList()
      .subscribe((reachList: OrganizationReach[]): OrganizationReach[] => this.reachList = reachList);
  }

  private observeFilterChanges(): void {
    // Search query
    this.searchQueryInput.valueChanges.pipe(debounceTime(200)).subscribe((value: string) => {
      this.searchQuery = value;
      this.loadStoryData();
    });
    // Goal
    this.goalSelect.valueChanges.subscribe((selectedGoalList: OrganizationGoal[] | null): void => {
      if (selectedGoalList !== null) {
        this.selectedGoalIdList = selectedGoalList.map((goal: OrganizationGoal) => goal.id);
        this.loadStoryData();
      }
    });
    // Heritage type
    this.heritageTypeSelect.valueChanges.subscribe((selectedHeritageTypeList: OrganizationHeritageType[] | null): void => {
      if (selectedHeritageTypeList !== null) {
        this.selectedHeritageTypeIdList = selectedHeritageTypeList.map((heritageType: OrganizationHeritageType) => heritageType.id);
        this.loadStoryData();
      }
    });
    // Impact type
    this.impactTypeSelect.valueChanges.subscribe((selectedImpactTypeList: OrganizationImpactType[] | null): void => {
      if (selectedImpactTypeList !== null) {
        this.selectedImpactTypeIdList = selectedImpactTypeList.map((impactType: OrganizationImpactType) => impactType.id);
        this.loadStoryData();
      }
    });
    // Province
    this.provinceSelect.valueChanges.subscribe((selectedProvinceList: OrganizationProvince[] | null): void => {
      if (selectedProvinceList !== null) {
        this.selectedProvinceIdList = selectedProvinceList.map((province: OrganizationProvince) => province.id);
        this.loadStoryData();
      }
    });
    // Reach
    this.reachSelect.valueChanges.subscribe((selectedReachList: OrganizationReach[] | null): void => {
      if (selectedReachList !== null) {
        this.selectedReachIdList = selectedReachList.map((reach: OrganizationReach) => reach.id);
        this.loadStoryData();
      }
    });
  }

  public openFiltersSheet(): void {
    this.matBottomSheet.open(FiltersBottomSheet);
  }

  /**
   * Clear all filters
   */
  public clearFilters(): void {
    this.resetFiltersInProgress = true;
    this.searchQueryInput.reset('', {emitEvent: false});
    this.goalSelect.reset([], {emitEvent: false});
    this.heritageTypeSelect.reset([], {emitEvent: false});
    this.impactTypeSelect.reset([], {emitEvent: false});
    this.provinceSelect.reset([], {emitEvent: false});
    this.reachSelect.reset([], {emitEvent: false});
    this.resetFiltersInProgress = false;

    // Also reset the value holder
    this.searchQuery = '';
    this.selectedGoalIdList = [];
    this.selectedHeritageTypeIdList = [];
    this.selectedImpactTypeIdList = [];
    this.selectedProvinceIdList = [];
    this.selectedReachIdList = [];

    // Reload data
    this.loadStoryData();
  }

  /**
   * Load filtered data from the back-end
   */
  public loadStoryData(): void {
    // Avoid loading when restoring the filters
    if (this.resetFiltersInProgress) {
      return;
    }
    // General filters and pagination
    const params: any = {
      search: this.searchQuery,
      'heritageGoals[]': this.selectedGoalIdList,
      'heritageTypes[]': this.selectedHeritageTypeIdList,
      'impactTypes[]': this.selectedImpactTypeIdList,
      'provinces[]': this.selectedProvinceIdList,
      'scopes[]': this.selectedReachIdList,
      active: true,
    };

    // Load data
    this.appStateStore.loadOrganizationListData({params});
  }

  /**
   * When not showing a organization, remove the related Open Linked Data schema
   */
  private publishJsonLdOnOrganizationOpen(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd): void => {
        if (event.urlAfterRedirects.includes('/neighbourhood/') === false) {
          // Remove old JSON-LD if exists
          const old: HTMLElement | null = this.document.getElementById('dynamic-jsonld');
          if (old) old.remove();
        }
      });
  }

}
